/**
 * Utilitários para a janela de 24h do WhatsApp.
 * Usado na lista de conversas e no header da conversa para sinalizar "prestes a expirar" ou "expirado".
 *
 * A janela do provedor é do cliente no canal — use `resolveMessagingWindowLastInboundDate`
 * com `messagingWindows` do details quando disponível.
 */

const WINDOW_HOURS = 24;
const WARNING_START_HOURS = 20;

const WHATSAPP_ORIGIN_TIPOS = new Set([6, 20, 24]);
const MESSENGER_ORIGIN_TIPOS = new Set([12]);
const INSTAGRAM_ORIGIN_TIPOS = new Set([21]);

/**
 * Calcula o estado da janela de 24h com base na data da última atividade (ou última mensagem do cliente).
 * @param {string | Date | number | null} lastDate - Data da última mensagem do cliente ou última atividade
 * @returns {{ status: 'ok' | 'aboutToExpire' | 'expired', hoursLeft: number | null, tooltip: string }}
 */
export function getWhatsAppWindowState(lastDate) {
  if (lastDate == null) {
    return {
      status: "ok",
      hoursLeft: null,
      tooltip: "",
    };
  }

  const date = typeof lastDate === "object" && lastDate instanceof Date
    ? lastDate
    : new Date(lastDate);

  if (Number.isNaN(date.getTime())) {
    return { status: "ok", hoursLeft: null, tooltip: "" };
  }

  const diffMs = Date.now() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < WARNING_START_HOURS) {
    const hoursLeft = Math.max(0, WINDOW_HOURS - diffHours);
    return {
      status: "ok",
      hoursLeft,
      tooltip: "",
    };
  }

  if (diffHours < WINDOW_HOURS) {
    const hoursLeft = Math.max(0, WINDOW_HOURS - diffHours);
    const rounded = Math.floor(hoursLeft);
    return {
      status: "aboutToExpire",
      hoursLeft,
      tooltip: `Janela de 24h do WhatsApp do cliente expira em aproximadamente ${rounded} ${rounded === 1 ? "hora" : "horas"}. Envie sua resposta ou use um template de reengajamento antes que a janela feche.`,
    };
  }

  return {
    status: "expired",
    hoursLeft: 0,
    tooltip: "A janela de 24 horas do WhatsApp deste cliente está fechada. Para enviar mensagens, use um template de reengajamento no campo de mensagem ou aguarde o cliente enviar uma nova mensagem.",
  };
}

function isClientSender(msg) {
  const senderType = (msg?.senderType || msg?.sender || "").toLowerCase();
  return (
    senderType === "client" ||
    senderType === "cliente" ||
    senderType === "customer" ||
    senderType === "user"
  );
}

function messageOriginTipo(msg) {
  const t = Number(msg?.originTipo ?? msg?.messageChannelTipo ?? msg?.channelTipo);
  return Number.isFinite(t) ? t : null;
}

function messageMatchesChannelFamily(msg, family) {
  const tipo = messageOriginTipo(msg);
  if (family === "whatsapp") {
    if (tipo != null) return WHATSAPP_ORIGIN_TIPOS.has(tipo);
    const ct = String(msg?.channelType || msg?.channel || "").toLowerCase();
    return ct.includes("whatsapp");
  }
  if (family === "messenger") {
    if (tipo != null) return MESSENGER_ORIGIN_TIPOS.has(tipo);
    const ct = String(msg?.channelType || msg?.channel || "").toLowerCase();
    return ct === "messenger" || ct === "facebook";
  }
  if (family === "instagram") {
    if (tipo != null) return INSTAGRAM_ORIGIN_TIPOS.has(tipo);
    const ct = String(msg?.channelType || msg?.channel || "").toLowerCase();
    return ct === "instagram" || ct.includes("instagram");
  }
  return false;
}

/**
 * Retorna a data da última mensagem do cliente a partir da lista de mensagens.
 * @param {Array} messages
 * @param {{ channelFamily?: 'whatsapp'|'messenger'|'instagram'|null, includeUntyped?: boolean }} [opts]
 * @returns {Date | null}
 */
export function getLastClientMessageDate(messages, opts = {}) {
  if (!Array.isArray(messages) || messages.length === 0) return null;

  const family = opts.channelFamily || null;
  const clientMessages = messages.filter((msg) => {
    if (!isClientSender(msg)) return false;
    if (!family) return true;
    const tipo = messageOriginTipo(msg);
    const hasChannelHint =
      tipo != null ||
      msg?.channelType ||
      msg?.channel ||
      msg?.messageChannelId ||
      msg?.originId;
    if (!hasChannelHint) return opts.includeUntyped === true;
    return messageMatchesChannelFamily(msg, family);
  });

  if (clientMessages.length === 0) return null;

  let lastDate = null;
  for (const msg of clientMessages) {
    const ts = msg.updatedAt || msg.lastUpdate || msg.timestamp || msg.date;
    if (!ts) continue;
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime()) && (!lastDate || d > lastDate)) {
      lastDate = d;
    }
  }
  return lastDate;
}

function parseInboundDate(value) {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Última inbound do cliente no canal: max(API por cliente, mensagens da thread no canal).
 */
export function resolveMessagingWindowLastInboundDate({
  messages,
  messagingWindows,
  channelFamily,
  conversationChannelFamily = null,
} = {}) {
  if (!channelFamily) return null;
  const fromApi = parseInboundDate(messagingWindows?.[channelFamily] ?? null);
  const includeUntyped = conversationChannelFamily === channelFamily;
  const fromThread = getLastClientMessageDate(messages, {
    channelFamily,
    includeUntyped,
  });
  if (!fromApi) return fromThread;
  if (!fromThread) return fromApi;
  return fromThread > fromApi ? fromThread : fromApi;
}

export function channelTypeToMessagingWindowFamily(channelType, channelTipo = null) {
  const tipo = channelTipo != null ? Number(channelTipo) : NaN;
  if (tipo === 12) return "messenger";
  if (tipo === 21) return "instagram";
  if (tipo === 6 || tipo === 20 || tipo === 24) return "whatsapp";
  const key = String(channelType || "")
    .trim()
    .toLowerCase();
  if (key === "instagram" || key.includes("instagram")) return "instagram";
  if (key === "messenger" || key === "facebook") return "messenger";
  if (key.includes("whatsapp")) return "whatsapp";
  return null;
}

export { WINDOW_HOURS, WHATSAPP_ORIGIN_TIPOS, MESSENGER_ORIGIN_TIPOS, INSTAGRAM_ORIGIN_TIPOS };
